import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, RefreshCcw, Truck, Lock, CreditCard } from 'lucide-react';

/**
 * TrustBadges — small reassurance strip for e-commerce flows.
 *
 * Placed near checkout CTAs (cart page, checkout form) to reduce drop-off.
 * Each badge links to the matching legal page so users can drill in.
 *
 * Variants:
 *   - "row"      — horizontal, default (desktop cart)
 *   - "compact"  — minimal, three icons (mobile)
 */
const TrustBadges = ({ variant = 'row' }) => {
  const { t } = useTranslation();

  const badges = [
    {
      key: 'ssl',
      icon: Lock,
      title: t('trust.ssl_title', 'SSL zabezpečenie'),
      subtitle: t('trust.ssl_sub', '256-bit šifrovanie'),
      link: null,
    },
    {
      key: 'return',
      icon: RefreshCcw,
      title: t('trust.return_title', '14 dní na vrátenie'),
      subtitle: t('trust.return_sub', 'Bez udania dôvodu'),
      link: '/legal/returns',
    },
    {
      key: 'shipping',
      icon: Truck,
      title: t('trust.shipping_title', 'Doprava zdarma'),
      subtitle: t('trust.shipping_sub', 'Od 1 000 €'),
      link: '/legal/shipping',
    },
    {
      key: 'payment',
      icon: CreditCard,
      title: t('trust.payment_title', 'Bezpečná platba'),
      subtitle: t('trust.payment_sub', 'Stripe · banka'),
      link: null,
    },
    {
      key: 'gdpr',
      icon: ShieldCheck,
      title: t('trust.gdpr_title', 'GDPR ochrana'),
      subtitle: t('trust.gdpr_sub', 'Vaše údaje sú v bezpečí'),
      link: '/legal/privacy',
    },
  ];

  const visible = variant === 'compact' ? badges.slice(0, 3) : badges;

  return (
    <div
      data-testid="trust-badges"
      className={`grid gap-3 ${
        variant === 'compact'
          ? 'grid-cols-3 text-center'
          : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'
      }`}
    >
      {visible.map(({ key, icon: Icon, title, subtitle, link }) => {
        const inner = (
          <div
            className={`flex ${
              variant === 'compact' ? 'flex-col items-center' : 'items-start gap-3'
            } p-3 rounded-lg bg-white border border-gray-200 ${
              link ? 'hover:border-orange-300 hover:shadow-sm transition cursor-pointer' : ''
            }`}
          >
            <div
              className={`flex-shrink-0 ${
                variant === 'compact' ? 'mb-1' : ''
              } h-10 w-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className={variant === 'compact' ? 'text-xs' : 'min-w-0'}>
              <div
                className={`font-semibold text-gray-900 ${
                  variant === 'compact' ? 'text-xs' : 'text-sm'
                }`}
              >
                {title}
              </div>
              <div
                className={`text-gray-500 ${
                  variant === 'compact' ? 'text-[10px]' : 'text-xs'
                }`}
              >
                {subtitle}
              </div>
            </div>
          </div>
        );
        return link ? (
          <a
            key={key}
            href={link}
            target="_blank"
            rel="noreferrer"
            data-testid={`trust-badge-${key}`}
            className="block"
          >
            {inner}
          </a>
        ) : (
          <div key={key} data-testid={`trust-badge-${key}`}>
            {inner}
          </div>
        );
      })}
    </div>
  );
};

export default TrustBadges;
